import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check, Plus, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useEquipment } from "@/hooks/useEquipment";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatEquipmentName } from "@/lib/equipment-utils";

interface EquipmentAutocompleteProps {
  value: { id: string; name: string; isTemporary: boolean } | null;
  onChange: (
    value: { id: string; name: string; isTemporary: boolean } | null,
  ) => void;
  onCreateTemporary: (name: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  tagIds?: string[];
}

export function EquipmentAutocomplete({
  value,
  onChange,
  onCreateTemporary,
  placeholder = "Search equipment...",
  autoFocus = false,
  tagIds,
}: EquipmentAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value?.name || "");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { data, isLoading } = useEquipment({ search, tagIds });
  const equipment = data?.items ?? [];

  useEffect(() => {
    if (value) {
      setSearch(value.name);
    }
  }, [value]);

  // Update dropdown position when open changes or on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (inputRef.current && open) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    if (open) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        inputRef.current &&
        !inputRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item: {
    id: string;
    name: string;
    manufacturer?: string | null;
  }) => {
    const displayName = formatEquipmentName(item.name, item.manufacturer);
    onChange({ id: item.id, name: displayName, isTemporary: false });
    setSearch(displayName);
    setOpen(false);
  };

  const handleCreateTemporary = () => {
    if (search.trim()) {
      onCreateTemporary(search.trim());
      setOpen(false);
    }
  };

  const dropdownInner = (
    <div className="max-h-60 overflow-auto p-1">
      {isLoading ? (
        <div className="px-2 py-1.5 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : (
        <>
          {equipment.length === 0 && !search && (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              Type to search equipment...
            </div>
          )}

          {equipment.map((item) => (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground",
                value?.id === item.id && "bg-accent",
              )}
              onClick={() => handleSelect(item)}
            >
              <Package className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  {formatEquipmentName(item.name, item.manufacturer)}
                </div>
                {item.number && (
                  <div className="text-xs text-muted-foreground">
                    {item.number}
                  </div>
                )}
              </div>
              {value?.id === item.id && (
                <Check className="h-4 w-4 flex-shrink-0" />
              )}
            </div>
          ))}

          {search.trim() && (
            <div
              className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground border-t mt-1 pt-1"
              onClick={handleCreateTemporary}
            >
              <Plus className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              <span>Create temporary: "{search}"</span>
            </div>
          )}
        </>
      )}
    </div>
  );

  // On mobile, render dropdown inline with absolute positioning
  // On desktop, use portal with fixed positioning to escape scroll containers
  const dropdownContent = open ? (
    isMobile ? (
      <div
        ref={dropdownRef}
        data-equipment-autocomplete
        className="absolute left-0 right-0 top-full mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-lg"
      >
        {dropdownInner}
      </div>
    ) : (
      <div
        ref={dropdownRef}
        data-equipment-autocomplete
        className="fixed z-[9999] rounded-md border bg-popover text-popover-foreground shadow-lg"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {dropdownInner}
      </div>
    )
  ) : null;

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full"
      />
      {dropdownContent &&
        (isMobile
          ? dropdownContent
          : createPortal(dropdownContent, document.body))}
    </div>
  );
}
